import Loader from "components/Loader/Loader";
import Popover from "components/Popover/Popover";
import { IOption } from "Dashboard/domain/IOption";
import { carnetService } from "Dashboard/scenes/Carnets/services/carnet.service";
import { useAppDispatch } from "hooks/hooks";
import { debounce } from "lodash";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useSelector } from "react-redux";
import { FormatDateIso, getFristDateOfCurrnetMonth } from "utils/dateUtils";
import classes from "../../../../../../components/Popover/popover.module.scss";
import classesCarnets from "../../../styles/popover.module.scss";
import {
  getCreatedCarnetsInPeriod,
  getHtzEntries,
  selectCarnet,
  selectState,
  setNoteForCarnetPopover,
  setSelectedDates,
  setSelectedEmployeeId,
  setSelectedProjectId,
  updateCarnet
} from "../carnets.slice";
import { selectState as selectStateDashboard } from "Dashboard/dashboard.slice";
import CarnetsFooter from "./CarnetsFooter";
import CarnetsHeader from "./CarnetsHeader";
import CarnetsRow from "./CarnetsRow";
import DeleteCarnetModal from "./DeleteCarnetModal";
import HtzFooter from "./HtzFooter";
import HtzHeader from "./HtzHeader";
import HtzRow from "./HtzRow";
import PrintCarnets from "./PrintCarnets";
import { DateRange } from "Dashboard/scenes/Carnets/domain/DateRange";
import { usePagePrint } from "hooks/usePagePrint";
import { toast } from "react-toastify";
import { useWindowedRows } from "hooks/useWindowedRows";

// Keep in sync with `.g-grid.g-content .row` height ($grid-content-row-height).
const ROW_HEIGHT = 20;

interface IProps {
  projects: IOption[];
};

const BottomSection = ({ projects }: IProps) => {
  const dispatch = useAppDispatch();
  const componentRef = useRef<HTMLDivElement>(null);
  const handlePrint = usePagePrint(componentRef,
    { margins: "1cm 0.3cm" });

  const {
    loading,
    carnets,
    totals,
    selectedDateFrom,
    selectedDateTo,
    selectedEmployeeId,
    selectedProjectId,
    htzEntries,
    noteForCarnetPopover,
    indexForCarnet
  } = useSelector(selectState);
  const carnet = useSelector(selectCarnet);
  const { provisions } = useSelector(selectStateDashboard);
  const hourlyRatesNotLoaded = !provisions?.activeHourlyRateNames;
  const hourlyRatesNotDefined = provisions?.activeHourlyRateNames
    && provisions.activeHourlyRateNames.length === 0;

  const { contentRef, startIndex, endIndex, topSpacer, bottomSpacer } =
    useWindowedRows({
      count: carnets?.length ?? 0,
      rowHeight: ROW_HEIGHT,
      overscanPx: 300 * ROW_HEIGHT,
    });

  const [selectedDateFromHtz, setSelectedDateFromHtz]
    = useState<string | undefined>(FormatDateIso(getFristDateOfCurrnetMonth()));
  const [selectedDateToHtz, setSelectedDateToHtz]
    = useState<string | undefined>(FormatDateIso(new Date()));
  const [selectedEmployeeIdHtz, setSelectedEmployeeIdHtz]
    = useState<number | undefined>(undefined);

  const selectDate = useCallback((data: DateRange) => {
    const { dateFrom, dateTo } = data;
    dispatch(setSelectedDates({
      dateFrom: dateFrom ? FormatDateIso(dateFrom) : undefined,
      dateTo: dateTo ? FormatDateIso(dateTo) : undefined,
    }));
  }, [dispatch]);

  const selectDateHtz = (data: DateRange) => {
    const { dateFrom, dateTo } = data;
    setSelectedDateFromHtz(
      dateFrom ? FormatDateIso(dateFrom) : undefined);
    setSelectedDateToHtz(
      dateTo ? FormatDateIso(dateTo) : undefined);
  };

  const selectEmployeeCarnets = (employeeId: number) => {
    dispatch(setSelectedEmployeeId({ employeeId }));
  };

  const selectProject = (projectId: number) => {
    dispatch(setSelectedProjectId({ projectId }));
  };

  const selectEmployeeHtz = (employeeId: number) => {
    setSelectedEmployeeIdHtz(employeeId);
  };

  useEffect(() => {
    return () => {
      dispatch(setSelectedEmployeeId({ employeeId: undefined }));
      dispatch(setSelectedProjectId({ projectId: undefined }));
    }
  }, [dispatch]);

  useEffect(() => {
    dispatch(getCreatedCarnetsInPeriod({
      from: selectedDateFrom,
      to: selectedDateTo,
      employeeId: selectedEmployeeId,
      projectId: selectedProjectId,
    }));
  }, [dispatch,
    selectedDateFrom,
    selectedDateTo,
    selectedEmployeeId,
    selectedProjectId]);

  useEffect(() => {
    dispatch(getHtzEntries({
      employeeId: selectedEmployeeIdHtz,
      from: selectedDateFromHtz,
      to: selectedDateToHtz
    }));
  }, [dispatch, selectedEmployeeIdHtz, selectedDateFromHtz, selectedDateToHtz]);

  const delayedNoteUpdate = useMemo(
    () =>
      debounce(async (entryId: number, index: number, value: string) => {
        try {
          const carnet = await carnetService.updateCarnetNote({
            carnetId: entryId,
            note: value,
          });
          dispatch(updateCarnet({ index, carnet }));
        } catch (err: any) {
          if (err.response.status === 409) {
            toast.warn("Nije dozvoljena izmena karneta koji je uneo drugi korisnik. Potreban vam je pun pristup modulu karneti.");
          } else {
            toast.error("Karneti nisu uneti zbog neočekivane greške.");
          }
        }
      }, 1000),
    [dispatch]
  );

  return (
    <>
      <section className="karnet-section">
        <div className="loading-container">
          {(loading || hourlyRatesNotLoaded || hourlyRatesNotDefined)
            && <Loader customMessage={
              hourlyRatesNotDefined
                ? "Nisu unete satnice u podešavanjima aplikacije"
                : undefined} />}
          <div className="g-grid">
            <CarnetsHeader
              projects={projects}
              totals={totals}
              selectDate={selectDate}
              selectedDateFrom={selectedDateFrom}
              selectedDateTo={selectedDateTo}
              selectEmployee={selectEmployeeCarnets}
              selectProject={selectProject}
              print={handlePrint}
            />
          </div>

          <div className="g-grid g-content" ref={contentRef}>
            {topSpacer > 0 && <div style={{ height: topSpacer }} />}
            {carnets &&
              carnets
                .slice(startIndex, endIndex)
                .map((carnet, offset) => {
                  const index = startIndex + offset;
                  return (
                    <CarnetsRow
                      carnet={carnet}
                      totals={totals}
                      index={index}
                      key={index}
                    />
                  );
                })}
            {bottomSpacer > 0 && <div style={{ height: bottomSpacer }} />}
          </div>
          <div className="g-grid g-header">
            <CarnetsFooter totals={totals}
            />
          </div>
        </div>
        <div className="loading-container htz-section">
          {loading && <Loader />}
          <div className="g-grid">
            <HtzHeader
              selectDate={selectDateHtz}
              selectedDateFrom={selectedDateFromHtz}
              selectedDateTo={selectedDateToHtz}
              selectEmployee={selectEmployeeHtz}
            />
          </div>

          <div className="g-grid g-content">
            {htzEntries &&
              htzEntries.map((htzEntry, index) => (
                <HtzRow
                  htzEntry={htzEntry}
                  index={index}
                  key={index}
                />
              ))}
          </div>
          <div className="g-grid g-header">
            <HtzFooter totals={totals} />
          </div>
        </div>
        <Popover
          isOpen={noteForCarnetPopover.isOpen}
          onClose={() =>
            dispatch(setNoteForCarnetPopover(
              { popoverState: { isOpen: false }, index: -1 }
            ))
          }
          title="napomena"
          direction="right"
          arrowStyle={{ top: "45%" }}
          position={{
            top: noteForCarnetPopover.top,
            left: noteForCarnetPopover.left,
            offsetLeft: 25,
            offsetTop: -65
          }}
        >
          <textarea
            className={`
            ${classes.noteText}
            ${classesCarnets.notePopover}  
          `}
            defaultValue={carnet?.note}
            onChange={(e) => {
              delayedNoteUpdate(
                carnet.carnetId, indexForCarnet, e.target.value);
            }}
          ></textarea>
        </Popover>
        <div ref={componentRef} className="print-area">
          <PrintCarnets />
        </div>
        <DeleteCarnetModal />
      </section>
    </>
  );
};

export default BottomSection;
